/**
 * An `<img>` that will not retry a URL the browser already rejected.
 *
 * The live grid virtualises rows, so a 404 logo would be requested again every
 * time the row scrolled back into view — four red console lines, forever.
 * A module-level set survives unmount; the proxy's 204 is the other half.
 */

import { useEffect, useState, type CSSProperties } from 'react';

const broken = new Set<string>();

interface SafeImageProps {
  src: string | null | undefined;
  className?: string;
  style?: CSSProperties;
  onLoad?: () => void;
}

export function SafeImage({ src, className, style, onLoad }: SafeImageProps) {
  const [failed, setFailed] = useState(() => Boolean(src && broken.has(src)));

  useEffect(() => {
    setFailed(Boolean(src && broken.has(src)));
  }, [src]);

  if (!src || failed) return null;

  return (
    <img
      src={src}
      alt=""
      className={className}
      style={style}
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
      onError={() => {
        broken.add(src);
        setFailed(true);
      }}
    />
  );
}